import { z } from 'zod';
import {
  REPORT_IDENTITY_DESCRIPTION_MAX_LENGTH,
  REPORT_IDENTITY_TITLE_MAX_LENGTH,
} from '@/domains/reports/utils/report-header.utils';

/** Line breaks that would make a report name wrap or appear empty in the UI. */
const REPORT_NAME_LINE_BREAKS = /[\r\n\u2028\u2029]+/g;

export function clampReportIdentityName(value: string): string {
  return value.slice(0, REPORT_IDENTITY_TITLE_MAX_LENGTH);
}

export function replaceReportNameLineBreaks(value: string): string {
  return value.replace(REPORT_NAME_LINE_BREAKS, ' ');
}

export function sanitizeReportNameInput(value: string): string {
  return clampReportIdentityName(replaceReportNameLineBreaks(value));
}

export function formatReportIdentityNameForSave(value: string): string {
  return replaceReportNameLineBreaks(value).trim();
}

export const reportIdentityNameSchema = z
  .string()
  .transform((value) => formatReportIdentityNameForSave(value))
  .pipe(
    z
      .string()
      .min(1, 'Title is required')
      .max(REPORT_IDENTITY_TITLE_MAX_LENGTH, `Title must be at most ${REPORT_IDENTITY_TITLE_MAX_LENGTH} characters`),
  );

export const reportIdentityDescriptionSchema = z
  .string()
  .max(
    REPORT_IDENTITY_DESCRIPTION_MAX_LENGTH,
    `Description must be at most ${REPORT_IDENTITY_DESCRIPTION_MAX_LENGTH} characters`,
  );

export const reportIdentityIconSchema = z
  .string()
  .nullable()
  .transform((value) => (value === '' ? null : value));

export const reportIdentityTitleFormSchema = z.object({
  name: reportIdentityNameSchema,
});

export const reportIdentityDescriptionFormSchema = z.object({
  description: reportIdentityDescriptionSchema,
});

export const reportIdentityIconFormSchema = z.object({
  icon: reportIdentityIconSchema,
});

export type ReportIdentityTitleFormValues = z.infer<typeof reportIdentityTitleFormSchema>;
export type ReportIdentityDescriptionFormValues = z.infer<typeof reportIdentityDescriptionFormSchema>;
export type ReportIdentityIconFormValues = z.infer<typeof reportIdentityIconFormSchema>;

export function clampReportIdentityDescription(value: string): string {
  return value.slice(0, REPORT_IDENTITY_DESCRIPTION_MAX_LENGTH);
}

export function formatReportIdentityDescriptionForSave(value: string): string {
  return value.trim().slice(0, REPORT_IDENTITY_DESCRIPTION_MAX_LENGTH);
}

export function getFormFieldErrorMessage(errors: unknown[]): string | undefined {
  const first = errors[0];
  if (!first) return undefined;
  if (typeof first === 'string') return first;
  if (typeof first === 'object' && first !== null && 'message' in first) {
    return String((first as { message: unknown }).message);
  }
  return undefined;
}
